import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { List } from 'react-virtualized';
import { CSS } from '@dnd-kit/utilities';
import {
	DndContext,
	closestCenter,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
} from '@dnd-kit/core';
import {
	SortableContext,
	sortableKeyboardCoordinates,
	rectSortingStrategy,
	useSortable,
} from '@dnd-kit/sortable';
import { restrictToParentElement } from '@dnd-kit/modifiers';
import ReactSelect from 'react-select';

import {
	getSurveys,
	updateSurvey,
	addSurvey as dispatchAddSurvey,
} from '../../../../../../store/actions/admin/surveys';
import { EventsState } from '../../../../../../store/types';
import {
	ColorKeys,
	CreateSurvey,
	LanguagesAbbr,
	PageModule,
	Survey,
	SurveyQuestion,
	SurveyQuestionType,
	TextOverrides,
} from '../../../../../../types/working-model';
import Icon, { ICONS, COLORS } from '../../../../../general-ui/icon';
import ModalComponent from '../../../../../general-ui/modal/modal';
import { useParams } from 'react-router';
import Checkbox from '../../../../../general-ui/checkbox/checkbox';
import TextInput from '../../../../../general-ui/text-input/text';
import { GetDefaultSurvey } from '../../../../../../store/utils/create-event';
import { showAlert } from '../../../../../general-ui/alert/alert-service';
import Switch from '../../../../../general-ui/switch/switch';
import './surveys.scss';
import {
	GetNewOption,
	GetNewQuestion,
	InsertSurvey,
	UpdateSurvey,
} from '../../../../../../connection/surveys';
import WaitingIndicator from '../../../../../general-ui/waiting-indicator/waiting-indicator';
import { updatePageModule } from '../../../../../../store/actions/admin/create-event/session';
import { getTextValue, updateTranslateKey } from '../../../../../../utils/utils';
import Controls from '../../session-module-controls/controls';
import { reactSelectStyles } from '../../../../../general-ui/react-select/react-select-styles';
import Divider from '../../../../../general-ui/divider/divider';

import '../../../../../live-event/session/session-modules/survey/survey.scss';
import { TypographyItem } from '../../../../../general-ui/typography-item/typography-item';
import classNames from 'classnames';
import { TemplateClassNames } from '../../../../../../types/template-layouts';
import EditableTextSlim from '../../../../../general-ui/editable-text/editable-text-slim';
import { EContentColorOverrideNames } from '../moduleControls/edit-section-colors-modal';
import Textarea from '../../../../../general-ui/textarea/textarea';
import EditorSurvey from './survey';
import LargeButton from '../../../../../general-ui/button/large-button';
import { ChecklistTitles } from '../../../../../../store/reducers/admin/assistant';
import { addChecklistItem } from '../../../../../../store/actions/authentication';
import { ParamsProps } from '../../../../../live-event/live-event';
import { getStylingOverrides } from '../../../../../live-event/utils';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '../../../../../general-ui/tooltip/tooltip';

// import RenderSurvey from './survey-row';

interface Props {
	page_module: PageModule;
	template: TemplateClassNames;
}

enum SurveySelectionSteps {
	select,
	create,
	edit,
	questionsList,
	question,
}

export default function Surveys(props: Props): JSX.Element | null {
	const { page_module, template } = props;
	const {
		CreateSessionReducer: { workingSession },
		AuthReducer: { token, user },
		SurveysReducer: { surveys },
	} = useSelector((state: EventsState) => state);

	const { language } = useParams() as ParamsProps;

	const baseLanguage = workingSession?.default_language as LanguagesAbbr;

	const { t } = useTranslation("session");

	const [modalOpen, setModalOpen] = useState(false);
	const [workingSurvey, setWorkingSurvey] = useState<Survey | CreateSurvey | null>(null);
	const [workingQuestion, setWorkingQuestion] = useState<SurveyQuestion | null>(null);
	const [selectedSurveys, setSelectedSurveys] = useState<number[]>([]);
	const [searchTerm, setSearchTerm] = useState('');
	const [activeStep, setActiveStep] = useState<SurveySelectionSteps>(SurveySelectionSteps.select);
	const [saving, setSaving] = useState(false);
	const content = { ...GetDefaultSurvey(), ...page_module?.content };
	const styling_overrides = page_module?.styling_overrides;
	const { backgroundColor, backgroundImg } = getStylingOverrides(styling_overrides);
	const sensors = useSensors(
		useSensor(PointerSensor),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		})
	);

	const dispatch = useDispatch();

	useEffect(() => {
		dispatch(getSurveys(token!));

		setSelectedSurveys(page_module.content_modules ?? []);
	}, [dispatch, page_module.content_modules, token]);

	function closeModal() {
		setModalOpen(false);
		setActiveStep(SurveySelectionSteps.select);
		setWorkingSurvey(null);
	}

	const checkSurvey = useCallback(
		(survey: number) => {
			return () => {
				if (selectedSurveys.includes(survey)) {
					setSelectedSurveys((selectedSurveys) =>
						selectedSurveys.filter((id) => id !== survey)
					);
				} else {
					setSelectedSurveys((selectedSurveys) => [
						...selectedSurveys,
						survey,
					]);
				}
			};
		},
		[selectedSurveys]
	);

	const visibleSurveys = useMemo(() => {
		if (!searchTerm) { return surveys; }

		return surveys.filter((survey: Survey) =>
			survey.name.base
				.toLowerCase()
				.trim()
				.replace(/ /g, '')
				.includes(searchTerm)
		);
	}, [searchTerm, surveys]);

	function editSurvey(survey: Survey) {
		return () => {
			setWorkingSurvey(survey);
			setActiveStep(SurveySelectionSteps.edit);
		};
	}

	function handleOpenSurveyModal() {
		setModalOpen(true);
	}

	const RenderSurvey = useCallback(
		(props: any) => {
			const {
				key, // Unique key within array of rows
				index, // Index of row within collection
				style,
			} = props;

			const survey = visibleSurveys[index];

			return (
				survey && (
					<div
						className='survey-row-container'
						key={key}
						style={style}
					>
						<div className={`survey-row ${selectedSurveys.includes(survey.survey) ? 'selected' : ''}`}>
							<div className="checkbox-and-data-container">
								<div className="checkbox-container">
									<Checkbox
										onChange={checkSurvey(survey.survey)}
										checked={selectedSurveys.includes(survey.survey)}
										value={survey.survey}
										color={'white'}
										activeColor={'cyan'}
									/>
								</div>
								<div className="survey-data">
									<p>{survey.name.base.length > 100 ? survey.name.base.substr(0, 100 - 3) + '...' : survey.name.base}</p>
									<span className="question-count">{`${survey
										.questions?.length ?? '0'} question${survey.questions?.length === 1 ? '' : 's'
										}`}</span>
								</div>
							</div>
							<div className="survey-actions">
								<button
									className="no-style"
									onClick={editSurvey(survey)}
								>
									<Icon
										name={ICONS.EDIT}
										size={16}
										color={COLORS.WHITE}
									/>
								</button>
							</div>
						</div>
					</div>
				)
			);
		},
		[selectedSurveys, visibleSurveys, checkSurvey]
	);

	const doneSelectingSurveys = useCallback(() => {
		dispatch(
			updatePageModule({
				...page_module,
				modules: selectedSurveys.map((selectedSurvey) =>
					surveys.find((survey) => survey.survey === selectedSurvey)
				),
				content_modules: selectedSurveys,
			})
		);

		// ASSISTANT CHECKLIST ITEM: complete users checklist item for updating a section
		if (
			user &&
			!user.onboarded.includes(ChecklistTitles.editSection)
		) dispatch(addChecklistItem(token!, user.id, user.onboarded, ChecklistTitles.editSection));

		setWorkingQuestion(null);
		setWorkingSurvey(null);
		setModalOpen(false);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedSurveys, surveys, page_module, dispatch]);

	function handleSearch({
		target: { value },
	}: React.ChangeEvent<HTMLInputElement>) {
		setSearchTerm(
			value
				.toLowerCase()
				.trim()
				.replace(/ /g, '')
		);
	}

	const createNewSurvey = useCallback(() => {
		if (language === workingSession?.default_language) {
			setWorkingSurvey(GetDefaultSurvey());
			setActiveStep(SurveySelectionSteps.create);
		} else {
			showAlert({
				message: 'Unable to Create',
				description:
					'You are only able to create new content from the default selected language.',
				type: 'error',
				duration: 5000,
			});
		}
	}, [language, workingSession?.default_language]);

	const setWorkingSurveyTransKey = useCallback((
		key: 'name' | 'description' | 'completion_message',
		value: string
	) => {
		setWorkingSurvey((workingSurvey) => {
			if (!workingSurvey) { return null; }

			return {
				...workingSurvey,
				[key]: updateTranslateKey({
					translateString: workingSurvey[key],
					input: value,
					baseLanguage,
					language
				}),
			};
		});
	}, [language, baseLanguage]);

	const setWorkingSurveyTitle = useCallback(({
		target: { value },
	}: React.ChangeEvent<HTMLInputElement>) => {
		setWorkingSurveyTransKey('name', value);
	}, [setWorkingSurveyTransKey]);

	const setWorkingSurveyDescription = useCallback(({
		target: { value },
	}: React.ChangeEvent<HTMLInputElement>) => {
		setWorkingSurveyTransKey('description', value);
	}, [setWorkingSurveyTransKey]);

	{/* FUTURE DEV: hiding the complete message for now until design complete */ }
	// const setWorkingSurveyCompletion = useCallback(({
	// 	target: { value },
	// }: React.ChangeEvent<HTMLInputElement>) => {
	// 	setWorkingSurveyTransKey('completion_message', value);
	// }, [setWorkingSurveyTransKey]);

	// function setSuccessThreshold({ target: { valueAsNumber } }: React.ChangeEvent<HTMLInputElement>) {
	// 	setWorkingSurvey((workingSurvey) => {
	// 		if (!workingSurvey) return null;

	// 		return {
	// 			...workingSurvey,
	// 			success_threshold: valueAsNumber
	// 		}
	// 	});
	// }

	function toggleAllowRetake(value: any, isOn: boolean) {
		setWorkingSurvey((workingSurvey) => {
			if (!workingSurvey) { return null; }

			return {
				...workingSurvey,
				allow_retake: isOn,
			};
		});
	}

	function toggleShowResults(value: any, isOn: boolean) {
		setWorkingSurvey((workingSurvey) => {
			if (!workingSurvey) { return null; }

			return {
				...workingSurvey,
				show_results: isOn,
			};
		});
	}

	function goToQuestions() {
		setWorkingQuestion(null);
		setActiveStep(SurveySelectionSteps.questionsList);
	}

	const handleDragEnd = useCallback((event: any) => {
		if (!workingSurvey) { return; }
		const questions = [...workingSurvey.questions];

		const startIndex = questions.findIndex(
			(question: SurveyQuestion) =>
				Number(question.survey_question) === Number(event.active.id)
		);
		const newIndex = questions.findIndex(
			(question: SurveyQuestion) =>
				Number(question.survey_question) === Number(event.over.id)
		);

		const [question] = questions.splice(startIndex, 1);
		questions.splice(newIndex, 0, question);

		setWorkingSurvey((survey) => {
			if (!survey) {
				return null;
			}

			return { ...survey, questions };
		});
	}, [workingSurvey]);

	function editQuestion(question: SurveyQuestion) {
		return () => {
			setWorkingQuestion(question);
			setActiveStep(SurveySelectionSteps.question);
		};
	}

	const handleDeleteSurvey = useCallback((question: SurveyQuestion) => async () => {
		if (!workingSurvey?.questions?.length) return;
		if ('survey' in workingSurvey) {
			// remove question from survey that has already been saved
			const updatedWorkingSurvey: Survey = {
				...workingSurvey,
				questions: workingSurvey.questions.filter(qn => qn.survey_question !== question.survey_question),
			};
			try {
				const updatedSurvey = await UpdateSurvey(token!, updatedWorkingSurvey);
				dispatch(updateSurvey(updatedSurvey));
				setWorkingSurvey(updatedSurvey);
			} catch (e) {
				console.error(e?.response || e);
			}
		} else {
			// removing question from new survey that has not been saved yet
			setWorkingSurvey({
				...workingSurvey,
				questions: workingSurvey.questions.filter(qn => qn.survey_question !== question.survey_question),
			});
		}
	}, [workingSurvey, dispatch, token]);

	const addQuestion = useCallback(async function() {
		const newQuestion = await GetNewQuestion(token!);
		setWorkingSurvey((workingSurvey) => {
			if (!workingSurvey) { return null; }

			return {
				...workingSurvey,
				questions: [...workingSurvey.questions, newQuestion],
			};
		});
	}, [token]);

	function setQuestionType(value: SurveyQuestionType) {
		setWorkingQuestion((question) => {
			if (!question) return null;
			return {
				...question,
				question_type: value,
			};
		});
	}

	const addOption = useCallback(async function() {
		const newOption = await GetNewOption(token!);

		setWorkingQuestion((question) => {
			if (!question) { return null; }

			return {
				...question,
				options: [...question.options, newOption],
			};
		});
	}, [token]);

	function setOptionLabel(index: number) {
		return ({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
			setWorkingQuestion((question) => {
				if (!question) { return null; }

				return {
					...question,
					options: question.options.map((option, _index) => {
						if (_index === index) {
							option.label.base = value;
						}
						return option;
					}),
				};
			});
		};
	}

	// function setOptionCorrect(index: number) {
	// 	return () => {
	// 		setWorkingQuestion(question => {
	// 			if (!question) return null;

	// 			return {
	// 				...question,
	// 				options: question.options.map((option, _index) => {
	// 					option.correct = _index === index;
	// 					return option;
	// 				})
	// 			}
	// 		});
	// 	}
	// }

	const setWorkingQuestionTitle = useCallback(({
		target: { value },
	}: React.ChangeEvent<HTMLInputElement>) => {
		setWorkingQuestion((question) => {
			if (!question) { return null; }

			return {
				...question,
				title: updateTranslateKey({
					translateString: question.title,
					input: value,
					baseLanguage,
					language
				}),
			};
		});
	}, [language, baseLanguage]);

	const modalTitle = useMemo(() => {
		switch (activeStep) {
			case SurveySelectionSteps.select:
				return 'Select Polls';
			case SurveySelectionSteps.edit:
				return 'Edit Poll';
			case SurveySelectionSteps.create:
				return 'Create Poll';
			case SurveySelectionSteps.question:
				return 'Edit Question';
			default:
				return 'Select Polls';
		}
	}, [activeStep]);

	const ModalBody = useMemo(() => {
		switch (activeStep) {
			case SurveySelectionSteps.select: {
				return (
					<>
						<div className="modal-search">
							<TextInput
								onChange={handleSearch}
								placeholder={'Search Surveys...'}
								icon={ICONS.SEARCH}
								size={'small'}
							/>
							<p>or</p>
							<button className="small" onClick={createNewSurvey} disabled={language !== baseLanguage}>
								Create Poll
							</button>
							{language !== baseLanguage && (
								<div className="survey-tooltip">
									<Tooltip tooltip="Speakers may only be created in the base language">
										<Icon name={ICONS.PRIMARY_TOOLTIP} color={COLORS.CYAN} size={12} />
									</Tooltip>
								</div>
							)}
						</div>
						<List
							width={560}
							height={560 - 112}
							rowCount={visibleSurveys.length}
							rowHeight={96 + 16}
							rowRenderer={RenderSurvey}
							overscanRowCount={12}
						/>
					</>
				);
			}
			case SurveySelectionSteps.edit: {
				return (
					<>
						<TextInput
							label={'Survey Title'}
							onChange={setWorkingSurveyTitle}
							defaultValue={workingSurvey?.name?.[language] as string ?? workingSurvey?.name.base}
							placeholder={'Enter a name for the survey'}
							id="survey-title"
						/>
						<TextInput
							label={'Survey Description'}
							onChange={setWorkingSurveyDescription}
							defaultValue={workingSurvey?.description?.[language] as string ?? workingSurvey?.description.base}
							placeholder={'Please enter a description'}
							id='survey-description'
						/>
						{/* FUTURE DEV: hiding the complete message for now until design complete */}
						{/* <TextInput
							label={'Message to user when complete'}
							onChange={setWorkingSurveyCompletion}
							defaultValue={
								workingSurvey?.completion_message.base ?? ''
							}
							placeholder={
								'Example: "Thank you for taking our survey!"'
							}
							id="completed-message"
						/> */}

						<div className="allow-retake">
							<div className="allow-retake-context">
								<label>Allow viewers to retake a survey</label>
								<span>
									Allow all attendees to retake a survey at
									any time.
								</span>
							</div>
							<Switch
								on={!!workingSurvey?.allow_retake}
								onClick={toggleAllowRetake}
								value={'allow-retake'}
							/>
						</div>
						<Divider />
						<div className="allow-retake">
							<div className="allow-retake-context">
								<label>Show results to the users</label>
								<span>
									Show results to all attendees after
									completing the survey.
								</span>
							</div>
							<Switch
								on={!!workingSurvey?.show_results}
								onClick={toggleShowResults}
								value={'show-results'}
							/>
						</div>
					</>
				);
			}
			case SurveySelectionSteps.create: {
				return (
					<>
						<TextInput
							label={'Survey Title'}
							onChange={setWorkingSurveyTitle}
							placeholder={'Enter a name for the survey'}
							id="survey-title"
						/>
						<TextInput
							label={'Survey Description'}
							onChange={setWorkingSurveyDescription}
							placeholder={'Please enter a description'}
							id='survey-description'
						/>
						{/* FUTURE DEV: hiding the complete message for now until design complete */}
						{/* <TextInput
							label={'Message to user when complete'}
							onChange={setWorkingSurveyCompletion}
							placeholder={
								'Example: "Thank you for taking our survey!"'
							}
							id="completed-message"
						/> */}

						<div className="allow-retake">
							<div className="allow-retake-context">
								<label>Allow viewers to retake a survey</label>
								<span>
									Allow all attendees to retake a survey at
									any time.
								</span>
							</div>
							<Switch
								on={!!workingSurvey?.allow_retake}
								onClick={toggleAllowRetake}
								value={'allow-retake'}
							/>
						</div>
						<Divider />
						<div className="allow-retake">
							<div className="allow-retake-context">
								<label>Show results to the users</label>
								<span>
									Show results to all attendees after
									completing the survey.
								</span>
							</div>
							<Switch
								on={!!workingSurvey?.show_results}
								onClick={toggleShowResults}
								value={'show-results'}
							/>
						</div>
					</>
				);
			}
			case SurveySelectionSteps.questionsList: {
				return (
					<div className="questions-list">
						<p className="subtitle">
							Surveys are used to test viewers on their knowledge
						</p>
						{!!workingSurvey && workingSurvey.questions.length > 0 ? (
							<DndContext
								sensors={sensors}
								collisionDetection={closestCenter}
								onDragEnd={handleDragEnd}
								modifiers={[restrictToParentElement]}
								autoScroll={false}
							>
								<SortableContext
									items={(workingSurvey!
										.questions as any[]).map(
											(question: SurveyQuestion) =>
												String(question.survey_question)
										)}
									strategy={rectSortingStrategy}
								>
									<div className="questions-list">
										{(workingSurvey!.questions as any[]).map(
											(question: SurveyQuestion, index: number) => (
												<QuestionComponent
													key={index}
													id={String(question.survey_question)}
													question={question}
													editQuestion={editQuestion}
													checkDeleteQuestion={() => null}
													handleDeleteSurvey={handleDeleteSurvey}
													language={language}
												/>
											)
										)}
									</div>
								</SortableContext>
							</DndContext>
						) : (
							<p className="survey-questions-empty">
								No questions have been added.
							</p>
						)}

						<button className="small" onClick={addQuestion}>
							<Icon name={ICONS.ADD} size={12} color={COLORS.WHITE} /> Add Question
						</button>
					</div>
				);
			}
			case SurveySelectionSteps.question: {
				const options = [
					{
						label: 'Select One',
						value: String(SurveyQuestionType.radio),
					},
					{
						label: 'Select Multiple',
						value: String(SurveyQuestionType.checkbox),
					},
					{
						label: 'Text Question',
						value: String(SurveyQuestionType.text),
					},
				];
				return (
					<div className="edit-question">
						<div className="question-row">
							<TextInput
								value={workingQuestion?.title?.[language] as string ?? workingQuestion?.title.base}
								onChange={setWorkingQuestionTitle}
								placeholder={'Enter question'}
								label={'Your Question'}
							/>
							<ReactSelect
								tabSelectsValue={false}
								styles={reactSelectStyles({
									control: {
										borderRadius: '100px',
									}
								})}
								options={options}
								onChange={value => setQuestionType(Number(value?.value))}
								defaultValue={
									workingQuestion?.question_type
										? options.find(option => String(option.value) === String(workingQuestion?.question_type))
										: {
											label: 'Select One',
											value: String(SurveyQuestionType.radio),
										}
								}
								menuPortalTarget={document.body}
							/>
						</div>

						{workingQuestion &&
							workingQuestion.question_type !==
							SurveyQuestionType.text && (
								<>
									{workingQuestion.options.length < 1 ? (
										<p className="survey-questions-empty">
											Please add at least one option.
										</p>
									) : (
										<>
											{workingQuestion.options.map(
												(option, index) => (
													<div
														className="survey-question-option"
														key={index}
													>
														<TextInput
															placeholder="Enter option text"
															defaultValue={
																option.label?.[language] as string ?? option.label.base
															}
															onChange={setOptionLabel(
																index
															)}
															label={`Option ${index + 1}`}
															id={option.label.base}
														/>
													</div>
												)
											)}
										</>
									)}
									<button
										className="small"
										onClick={addOption}
									>
										Add Option
									</button>
								</>
							)}
					</div>
				);
			}
			default: {
				return <div></div>;
			}
		}
	}, [
		activeStep,
		createNewSurvey,
		visibleSurveys.length,
		RenderSurvey,
		setWorkingSurveyTitle,
		workingSurvey,
		setWorkingSurveyDescription,
		sensors,
		handleDragEnd,
		addQuestion,
		workingQuestion,
		setWorkingQuestionTitle,
		addOption,
		handleDeleteSurvey,
		language,
		baseLanguage
	]);

	const finishedEditingQuestion = useCallback(() => {
		setWorkingSurvey((survey) => {
			if (!survey) { return null; }

			return {
				...survey,
				questions: survey.questions.map((question) => {
					if (
						workingQuestion &&
						question.survey_question === workingQuestion.survey_question
					) {
						return workingQuestion;
					}

					return question;
				}),
			};
		});

		goToQuestions();
	}, [workingQuestion]);

	const saveSurvey = useCallback(async function() {
		try {
			if (!workingSurvey) { return; }

			setSaving(true);
			if ('survey' in workingSurvey) {
				const updatedSurvey = await UpdateSurvey(token!, workingSurvey);
				dispatch(updateSurvey(updatedSurvey));
				setWorkingSurvey(updatedSurvey);
			} else {
				const newSurvey = await InsertSurvey(
					token!,
					workingSurvey as CreateSurvey
				);
				dispatch(dispatchAddSurvey(newSurvey));
			}

			setSaving(false);
			setActiveStep(SurveySelectionSteps.select);
		} catch (e) {
			console.error(e);
		}
	}, [dispatch, token, workingSurvey]);


	function handleTextChange(key: string) {
		return (text: string) => {
			if (!workingSession) return;
			dispatch(
				updatePageModule({
					...page_module,
					content: {
						...content,
						[key]: updateTranslateKey({
							translateString: page_module?.content?.[key],
							input: text,
							baseLanguage: workingSession.default_language,
							language
						}),
					},
				})
			);
		};
	}

	function handleStyleOverrideChange(key: string) {
		return (overrides: TextOverrides) => {
			const updatedOverrides = {
				...styling_overrides,
				content: {
					...styling_overrides?.content,
					[key]: overrides
				}
			};
			dispatch(
				updatePageModule({
					...page_module,
					styling_overrides: updatedOverrides
				})
			);
		};
	}

	const ModalFooter = useMemo(() => {

		function onlySpaces(input: string): boolean {
			// check to see if inpout string is only spaces
			const output = input.replace(/\s/g, '');
			if (output.length) return false;
			return true;
		}

		function goToQuestionsDisabled() {
			if (activeStep === SurveySelectionSteps.create) {
				return !(
					workingSurvey?.name.changed &&
					workingSurvey?.description.changed &&
					!onlySpaces(workingSurvey?.name.base) &&
					!onlySpaces(workingSurvey?.description.base)
					// FUTURE DEV: when completion message added in
					// add this line to validation
					// workingSurvey?.completion_message.changed
				);
			} else {
				return !(
					workingSurvey?.name.base &&
					workingSurvey?.description.base &&
					!onlySpaces(workingSurvey?.name.base) &&
					!onlySpaces(workingSurvey?.description.base)
					// FUTURE DEV: when completion message added in
					// add this line to validation
					// workingSurvey?.completion_message.base
				);
			}
		}

		function createSurveyDisabled() {
			if (workingSurvey) {
				//is disabled if the working survey has no questions
				if (workingSurvey.questions.length < 1) {
					return true;
				}

				for (const question of workingSurvey.questions) {
					if (!question.title.base.length || onlySpaces(question.title.base)) {
						return true;
					}
				}
			} else {
				return true;
			}
		}

		function updateQuestionDisabled() {
			if (
				workingQuestion?.question_type === SurveyQuestionType.checkbox ||
				workingQuestion?.question_type === SurveyQuestionType.radio
			) {
				if (!workingQuestion.options.length) {
					return true;
				}
				for (const option of workingQuestion.options) {
					if (!option.label.base.length || onlySpaces(option.label.base)) {
						return true;
					}
				}

			}

			return false;
		}

		const handleGoBack = (step: SurveySelectionSteps) => () => {
			setActiveStep(step);
		};

		switch (activeStep) {
			case SurveySelectionSteps.select: {
				return (
					<>
						<button
							onClick={closeModal}
							style={{ marginRight: 16 }}
						>
							Cancel
						</button>
						<button
							onClick={doneSelectingSurveys}
							className="primary"
						>
							Submit
						</button>
					</>
				);
			}

			case SurveySelectionSteps.edit:
			case SurveySelectionSteps.create: {
				return (
					<>
						<button
							onClick={closeModal}
							style={{ marginRight: 16 }}
						>
							Cancel
						</button>
						<button
							onClick={goToQuestions}
							disabled={goToQuestionsDisabled()}
							className="primary"
						>
							Go to Questions
						</button>
					</>
				);
			}

			case SurveySelectionSteps.questionsList: {
				return workingSurvey && !('survey' in workingSurvey) ? (
					<>
						<button
							onClick={closeModal}
							style={{ marginRight: 16 }}
						>
							Cancel
						</button>
						<button
							onClick={saveSurvey}
							className="primary"
							disabled={createSurveyDisabled()}
						>
							Create Survey
						</button>
					</>
				) : (
					<>
						<button
								onClick={handleGoBack(SurveySelectionSteps.edit)}
							style={{ marginRight: 16 }}
						>
							Back
						</button>
						<button
							onClick={saveSurvey}
							className="primary"
							disabled={createSurveyDisabled()}
						>
								{saving ? <WaitingIndicator /> : 'Save'}
						</button>
					</>
				);
			}

			case SurveySelectionSteps.question: {
				return workingQuestion &&
					!('survey_question' in workingQuestion) ? (
					<>
						<button
							onClick={goToQuestions}
							style={{ marginRight: 16 }}
						>
							Cancel
						</button>
						<button
							onClick={finishedEditingQuestion}
							className="primary"
							disabled={updateQuestionDisabled()}
						>
							Add Question
						</button>
					</>
				) : (
					<>
						<button
							onClick={goToQuestions}
							style={{ marginRight: 16 }}
						>
							Back
						</button>
						<button
							onClick={finishedEditingQuestion}
							className="primary"
							disabled={updateQuestionDisabled()}
						>
							Edit Question
						</button>
					</>
				);
			}

			default: {
				return (
					<>
						<button
							onClick={closeModal}
							style={{ marginRight: 16 }}
						>
							Cancel
						</button>
						<button
							onClick={doneSelectingSurveys}
							className="primary"
						>
							Done
						</button>
					</>
				);
			}
		}
	}, [
		activeStep,
		workingSurvey,
		doneSelectingSurveys,
		saveSurvey,
		saving,
		workingQuestion,
		finishedEditingQuestion,
	]);

	// quite a large unused function to just go ahead and delete and will most likely be used,
	// so disabling lint warning for this line
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	function displayQuestion(question: SurveyQuestion) {
		if (!question) return null;
		if (question.question_type === SurveyQuestionType.text) {
			return (
				<div
					className="survey-results text-result question-container"
					key={question.survey_question}
				>
					<TypographyItem className={classNames('question-title evt-heading-4', template)}>
						{question?.title?.[language] ?? question?.title?.base}
					</TypographyItem>
					<div className="survey-text-actions-container">
						<Textarea placeholder={t("Enter your repsonse here")} />
						<div className={classNames('submit-action')}><button className={classNames('primary next')} type="submit" >{content?.callToAction?.button_name?.[language] || "Submit"}</button></div>
					</div>
				</div>
			);
		}

		if (question.question_type === SurveyQuestionType.radio) {
			let remaining = 100;

			const percentages = question.options.map(() => {
				if (remaining > 1) {
					const amount = Math.floor(Math.random() * 60);
					remaining = remaining - amount;
					return amount;
				} else {
					return 0;
				}
			});

			if (remaining > 0) {
				percentages[percentages.length - 1] =
					remaining + percentages[percentages.length - 1];
			}

			return (
				<div
					key={question.survey_question}
					className="survey-results radio-results question-container"
				>
					<TypographyItem className={classNames('question-title evt-heading-4', template)}>
						{question.title[language] ?? question.title.base}
					</TypographyItem>
					{question.options.map((option, index) => (
						<div
							key={option.survey_question_option}
							className="survey-option-wrapper"
						>
							<div className="survey-option-container">
								<div
									className="survey-option focus-visible"
								>
									<div className="survey-option-progress-bar-container">
										<div className="survey-option-progress-bar" style={{ width: `${percentages[index]}%` }} />
									</div>
									<div className="survey-option-content">
										<div className="survey-option-title">{option.label[language] ?? option.label.base}</div>
										<div className="survey-option-check">
											<div>
												<Icon name={ICONS.CHECK_CIRCLE_FILLED} color={COLORS.WHITE} size={16} />
											</div>
											<div className="survey-option-percentage-text">{percentages[index]}%</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					))}
					<div className={classNames('submit-action')}><button className={classNames('primary next')} type="submit" >{content?.callToAction?.button_name?.[language] || "Submit"}</button></div>
				</div>
			);
		}

		if (question.question_type === SurveyQuestionType.checkbox) {
			const amounts = question.options.map(() => {
				return Math.min(100, Math.floor(Math.random() * 100));
			});

			return (
				<div
					key={question.survey_question}
					className="survey-results radio-results question-container"
				>
					<TypographyItem className={classNames('question-title evt-heading-4', template)}>
						{question.title[language] ?? question.title.base}
					</TypographyItem>
					{question.options.map((option, index) => (
						<div
							key={option.survey_question_option}
							className="survey-option-wrapper"
						>
							<div className="survey-option-container">
								<div
									className="survey-option focus-visible"
								>
									<div className="survey-option-progress-bar-container">
										<div className="survey-option-progress-bar" style={{ width: `${amounts[index]}%` }} />
									</div>
									<div className="survey-option-content">
										<div className="survey-option-title">{option.label[language] ?? option.label.base}</div>
										<div className="survey-option-check">
											<div>
												<Icon name={ICONS.CHECK_CIRCLE_FILLED} color={COLORS.WHITE} size={16} />
											</div>
											<div className="survey-option-percentage-text">{amounts[index]}%</div>
										</div>
									</div>
								</div>
							</div>
						</div>

					))}
					<div className={classNames('submit-action')}><button className={classNames('primary next')} type="submit" >{content?.callToAction?.button_name?.[language] || "Submit"}</button></div>
				</div>
			);
		}
	}
	const { custom_heading } = getTextValue(content, language);

	return page_module.is_on && (!workingSession?.layout?.withMenu || page_module) ? (
		<div className={classNames(
			"editor-wrapper surveys",
			backgroundColor,
			template
		)}
			style={backgroundImg}
		>
			<Controls
				openEditModal={handleOpenSurveyModal}
				page_module={page_module}
			/>
			<div className="container">
				<EditableTextSlim
					tagType="h5"
					className="ev-heading-5 surveys-custom-heading"
					value={custom_heading}
					defaultColor={ColorKeys.bodyTextColor}
					onChange={handleTextChange('custom_heading')}
					onOverrideChange={handleStyleOverrideChange('custom_heading')}
					elementName={EContentColorOverrideNames.custom_heading}
					colorOverride={styling_overrides?.content?.custom_heading?.color}
				/>
				<div className="surveys-content">
					{page_module.modules?.length ?
						page_module.modules.map((survey: Survey) => (
							<EditorSurvey key={survey.module_id} content={content} survey={survey} lang={language} template={template} />
						))
						: <LargeButton onClick={handleOpenSurveyModal} title={'Add Poll'} />}
				</div>
			</div>

			<ModalComponent
				cancellable={true}
				closeable={false}
				open={modalOpen}
				onRequestClose={closeModal}
				title={modalTitle}
				footer={ModalFooter}
			>
				<div className="surveys-modal">
					{ModalBody}
				</div>
			</ModalComponent>
		</div>
	) : null;
}

interface SortableQuestion {
	id: string;
	question: SurveyQuestion;
	editQuestion: (question: SurveyQuestion) => () => void;
	checkDeleteQuestion: (question: SurveyQuestion) => void;
	handleDeleteSurvey: (question: SurveyQuestion) => () => void;
	language: LanguagesAbbr
}

function QuestionComponent(props: SortableQuestion) {
	const { attributes, listeners, setNodeRef, transform } = useSortable({
		id: props.id,
	});

	const { question, editQuestion, handleDeleteSurvey, language } = props;

	const style = {
		transform: CSS.Transform.toString(transform),
		transition: 'none',
	};

	const validMultiple = useMemo(() => {
		// if (question.multiple_choice) {
		// 	return question.options.some((option) => option.correct);
		// }
		if (question?.question_type !== SurveyQuestionType.text) {
			return question?.options?.length > 0;
		}

		return true;
	}, [question]);

	const type = useMemo(() => {
		switch (question.question_type) {
			case SurveyQuestionType.text:
				return 'Text area';
			case SurveyQuestionType.radio:
				return 'Select one';
			case SurveyQuestionType.checkbox:
				return 'Select multiple';
		}
	}, [question]);

	if (!question) return null;
	return (
		<div
			ref={setNodeRef}
			style={style}
			{...attributes}
			{...listeners}
			className="survey-question"
		>
			<div className="drag-handle">
				<Icon
					name={ICONS.DRAG_HANDLE}
					size={18}
					color={COLORS.DEFAULT_GRAY}
				/>
			</div>
			<div className="survey-question-info">
				<label>{question?.title?.[language] ?? question?.title?.base}</label>
				<span>
					{type}{' '}
					{!validMultiple && (
						<span className="invalid-multiple">
							(at least one option required)
						</span>
					)}
				</span>
			</div>
			<div className="survey-question-actions">
				<button className="no-style" onClick={editQuestion(question)}>
					<Icon name={ICONS.EDIT} size={16} color={COLORS.WHITE} />
				</button>
				<button onClick={handleDeleteSurvey(question)} className="no-style">
					<Icon name={ICONS.TRASH} size={16} color={COLORS.WHITE} />
				</button>
			</div>
		</div>
	);
}
